import { getUserId } from '../user/getUserId';

// 先定义类型
type StorageType = 'local' | 'session';

type JsonPrimitive = string | number | boolean | null;
type JsonValue = JsonPrimitive | JsonObject | JsonArray;
interface JsonObject {
  [key: string]: JsonValue;
}
interface JsonArray extends Array<JsonValue> {}

interface StorageOptions {
  storageKey: string;
  storageType?: StorageType;
}

/**
 * 存储工具类
 * 通过此工具进行localStorage和sessionStorage操作，会自动根据当前登录用户的userid来创建对应的存储空间
 */
class StorageTool {
  private readonly storageKey: string;
  private readonly userId: string;
  private readonly storage: Storage;

  // 错误类型定义
  private static readonly Errors = {
    QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
    INVALID_VALUE: 'INVALID_VALUE',
    NO_BROWSER: 'NO_BROWSER',
  } as const;

  /**
   * 创建存储工具实例
   * @param options - 配置选项
   * @param options.storageKey - 存储项目key
   * @param options.storageType - 存储器类型：'local' | 'session'
   */
  constructor(options: StorageOptions) {
    if (typeof window === 'undefined') {
      throw new Error('StorageTool 只能在浏览器环境中使用');
    }

    const { storageKey, storageType = 'local' } = options;

    this.storageKey = storageKey;
    this.userId = getUserId() || 'customer';

    const storage = window[`${storageType}Storage`];
    if (!storage) {
      throw new Error(`${storageType}Storage 在 window 对象上不存在`);
    }
    this.storage = storage;
  }

  /**
   * 获取用户存储空间下指定字段的值
   * @param key - 字段名
   * @param isCustomer - 是否使用通用存储空间
   * @param projectKey - 空间名，用于获取别的空间的值
   */
  public get<T extends JsonValue>(
    key: string,
    isCustomer = false,
    projectKey = this.storageKey,
  ): T | null {
    try {
      const userStorage = this.getUserAll(isCustomer, projectKey);
      if (!userStorage) return null;
      if (!key) return userStorage as T;
      return (userStorage[key] as T) || null;
    } catch (error) {
      this.handleError(error, 'get');
      return null;
    }
  }

  /**
   * 将值存入用户存储空间
   * @param key - 字段名
   * @param value - 要存储的值
   * @param isCustomer - 是否使用通用存储空间
   */
  public set<T extends JsonValue>(key: string, value: T, isCustomer = false): void {
    try {
      const userStorage = this.getUserAll(isCustomer);
      userStorage[key] = value;
      this.setUserAll(userStorage, isCustomer);
    } catch (error) {
      this.handleError(error, 'set');
    }
  }

  /**
   * 删除用户存储空间中的某个字段
   * @param key - 要删除的字段名
   * @param isCustomer - 是否使用通用存储空间
   */
  public rm(key: string, isCustomer = false): void {
    try {
      const userStorage = this.getUserAll(isCustomer);
      if (userStorage?.[key]) {
        delete userStorage[key];
        this.setUserAll(userStorage, isCustomer);
      }
    } catch (error) {
      this.handleError(error, 'rm');
    }
  }

  /**
   * 获取项目的所有存储数据
   * @param projectKey - 空间名，用于获取不同的空间中的字段
   */
  public getAll(projectKey = this.storageKey): Record<string, JsonValue> {
    try {
      const data = this.storage.getItem(projectKey);
      return data ? JSON.parse(data) : {};
    } catch (error) {
      this.handleError(error, 'getAll');
      return {};
    }
  }

  /**
   * 替换项目的全部存储数据
   * @param data - 存储数据对象
   */
  public setAll(data: Record<string, JsonValue>): void {
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(data));
    } catch (error) {
      this.handleError(error, 'setAll');
    }
  }

  /**
   * 获取用户的所有存储数据
   * @param isCustomer - 是否使用通用存储空间
   * @param projectKey - 空间名，用于获取不同的空间中的字段
   */
  private getUserAll(isCustomer = false, projectKey = this.storageKey): Record<string, JsonValue> {
    const allData = this.getAll(projectKey);
    const userId = isCustomer ? 'customer' : this.userId;
    return (allData[userId] as Record<string, JsonValue>) || {};
  }

  /**
   * 设置用户的所有存储数据
   * @param data - 存储数据对象
   * @param isCustomer - 是否使用通用存储空间
   */
  private setUserAll(data: Record<string, JsonValue>, isCustomer = false): void {
    const allData = this.getAll();
    const userId = isCustomer ? 'customer' : this.userId;
    allData[userId] = data;
    this.setAll(allData);
  }

  /**
   * 错误处理方法
   */
  private handleError(error: unknown, operation: string): void {
    if (error instanceof Error) {
      if (error.name === 'QuotaExceededError') {
        throw new Error(`${StorageTool.Errors.QUOTA_EXCEEDED}: 存储空间已满`);
      }
      console.warn(`StorageTool ${operation} 失败:`, error);
    }
  }

  /**
   * 批量设置存储项
   * @param items - 要存储的键值对
   * @param isCustomer - 是否使用通用存储空间
   */
  public setMultiple(items: Record<string, JsonValue>, isCustomer = false): void {
    const userStorage = this.getUserAll(isCustomer);
    Object.entries(items).forEach(([key, value]) => {
      userStorage[key] = value;
    });
    this.setUserAll(userStorage, isCustomer);
  }

  /**
   * 清空用户存储空间
   * @param isCustomer - 是否清空通用存储空间
   */
  public clean(isCustomer = false): void {
    try {
      const allData = this.getAll();
      const userId = isCustomer ? 'customer' : this.userId;
      if (allData[userId]) {
        delete allData[userId];
        this.setAll(allData);
      }
    } catch (error) {
      this.handleError(error, 'clean');
    }
  }
}

/**
 * 创建本地存储工具实例
 * @param storageKey - 项目存储标识
 */
export function createLocalStorage(storageKey: string): StorageTool {
  return new StorageTool({ storageKey, storageType: 'local' });
}

/**
 * 创建会话存储工具实例
 * @param storageKey - 项目存储标识
 */
export function createSessionStorage(storageKey: string): StorageTool {
  return new StorageTool({ storageKey, storageType: 'session' });
}